import { Input_ProductContent } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_ProductContent/Input_ProductContent.ts';
import { MODE } from '@services/MarppleShopStudio/widget/CreatorProduct/core/types/types.ts';
import { html, Html } from 'rune-ts';
import klass from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_ProductContent/Input_ProductContent.module.scss';
import { embedConfigs } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_ProductContent/embed-service.ts';
import { ImageUploader } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_ProductContent/views/ImageUploader/ImageUploader.ts';
import { Lang, Localized } from '../../../../../../../../shared/type';
import {
  PureInput,
  pureInputVariant,
} from '../../../../../../../../shared/rune-component/Input/PureInput.ts';
import { unEscaper } from '../../../../../../../../shared/utils/escape.ts';
import { shareFont } from '@services/MarppleShop/shared/typography';
import { entries, pipe, toArray, map, each } from '@fxts/core';
import { classes } from '@services/MarppleShop/shared/util';
import { INTL_ICONS } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/constants.ts';
import { formKeyMap } from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/shared.ts';

export class Input_ProductContent_Intl<TMode extends MODE> extends Input_ProductContent<TMode> {
  private readonly intl_inputs: Localized<{
    title: PureInput;
    description: PureInput;
  }> = {
    kr: {
      title: new PureInput({
        variant: pureInputVariant.studio_product_content,
        name: 'title',
        value: unEscaper(this.store.formData?._?.product?._?.product_story?.title ?? ''),
        onFocusout: (valueObj) => {
          this.updateProductStory({ title: valueObj.getValue() });
        },
        placeholder: '제목을 작성해주세요.',
        min_rows: 1,
        max_rows: 5,
        font_klass: shareFont('24_bold'),
      }),
      description: new PureInput({
        variant: pureInputVariant.studio_product_content,
        name: 'description',
        value: unEscaper(this.store.formData?._?.product?._?.product_story?.description ?? ''),
        onFocusout: (valueObj) => {
          this.updateProductStory({ description: valueObj.getValue() });
        },
        placeholder: '내용을 작성해주세요.',
        min_rows: 1,
        max_rows: 1000,
        font_klass: shareFont('14_medium'),
      }),
    },

    en: {
      title: new PureInput({
        variant: pureInputVariant.studio_product_content,
        name: 'title_en',
        value: unEscaper(this.store.formData?._?.product?._?.product_story?.title_en ?? ''),
        onFocusout: (valueObj) => {
          this.updateProductStory({ title_en: valueObj.getValue() });
        },
        placeholder: '영문 제목을 작성해주세요.',
        min_rows: 1,
        max_rows: 5,
        font_klass: shareFont('24_bold'),
      }),
      description: new PureInput({
        variant: pureInputVariant.studio_product_content,
        name: 'description_en',
        value: unEscaper(this.store.formData?._?.product?._?.product_story?.description_en ?? ''),
        onFocusout: (valueObj) => {
          this.updateProductStory({ description_en: valueObj.getValue() });
        },
        placeholder: '영문 내용을 작성해주세요.',
        min_rows: 1,
        max_rows: 1000,
        font_klass: shareFont('14_medium'),
      }),
    },

    jp: {
      title: new PureInput({
        variant: pureInputVariant.studio_product_content,
        name: 'title_jp',
        value: unEscaper(this.store.formData?._?.product?._?.product_story?.title_jp ?? ''),
        onFocusout: (valueObj) => {
          this.updateProductStory({ title_jp: valueObj.getValue() });
        },
        placeholder: '일문 제목을 작성해주세요.',
        min_rows: 1,
        max_rows: 5,
        font_klass: shareFont('24_bold'),
      }),
      description: new PureInput({
        variant: pureInputVariant.studio_product_content,
        name: 'description_jp',
        value: unEscaper(this.store.formData?._?.product?._?.product_story?.description_jp ?? ''),
        onFocusout: (valueObj) => {
          this.updateProductStory({ description_jp: valueObj.getValue() });
        },
        placeholder: '일문 내용을 작성해주세요.',
        min_rows: 1,
        max_rows: 1000,
        font_klass: shareFont('14_medium'),
      }),
    },
  };

  private selected_lang: Lang = Lang.kr;

  private setSelectedLang(lang: Lang) {
    this.selected_lang = lang;
  }

  private makeIntlTabHtml() {
    return html`
      <div class="${klass.tabs}">
        ${pipe(
          [
            {
              lang: Lang.kr,
              label: '국문 상세',
              icon: INTL_ICONS.kr,
            },
            {
              lang: Lang.en,
              label: '영문 상세',
              icon: INTL_ICONS.en,
            },
            {
              lang: Lang.jp,
              label: '일문 상세',
              icon: INTL_ICONS.jp,
            },
          ],
          map(
            (tab) => html`
              <div
                data-lang="${tab.lang}"
                class="${classes(klass.tab, {
                  [klass.active]: tab.lang === this.selected_lang,
                })}"
              >
                ${tab.icon}
                <span>${tab.label}</span>
              </div>
            `,
          ),
          toArray,
        )}
      </div>
    `;
  }

  protected override inputTemplate(): Html {
    return html`
      <div class="${klass.root} ${klass.intl_root}">
        ${this.makeIntlTabHtml()}
        <div class="${klass.body}">
          ${this.makeTitleAndContentHtml()}
          <div class="${klass.embed_wrap}">
            ${Object.values(embedConfigs).map((config) => this.makeEmbedHtml(config))}
          </div>
          ${new ImageUploader({
            images: this.store.formData?._?.product?._?.product_story?._?.product_story_images,
            onChange: (images) => {
              this.store.actions.setField(formKeyMap.product_story_images, images);
            },
          })}
        </div>
      </div>
    `;
  }

  protected override makeTitleAndContentHtml() {
    return html`
      <div class="${klass.content}">
        ${pipe(
          entries(this.intl_inputs),
          map(
            ([lang, intl_input]) => html`
              <div
                data-lang="${lang}"
                class="${classes(klass.content_wrap, {
                  [klass.active]: lang === this.selected_lang,
                })}"
              >
                ${intl_input.title} ${intl_input.description}
              </div>
            `,
          ),
          toArray,
        )}
      </div>
    `;
  }

  protected override onRender() {
    this.delegate('click', `.${klass.tabs}`, (event) => {
      const $target = event.target as HTMLElement;
      const $tab = $target.closest<HTMLElement>('[data-lang]');
      const lang = $tab?.dataset?.lang as Lang;

      if (!lang) return;

      this.setSelectedLang(lang);

      pipe(
        this.element().querySelectorAll<HTMLElement>('[data-lang]'),
        each((element) => {
          element.classList.toggle(klass.active, this.selected_lang === element.dataset.lang);
        }),
      );
    });
  }
}
